import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/App.css";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Admin from "./pages/Admin";
import LandingPage from "./pages/LandingPage";
import ContactPage from "./pages/ContactPage";
import { ContentProvider } from "./content-context";
import PageLayout from "./components/PageLayout";
import ConsentBanner from "./components/ConsentBanner";
import { TomsBar, AdventureRelax, Occasions, MinutesFromWestport } from "./components/HomeSections";
import Preloader from "./components/Preloader";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Island from "./components/Island";
import Accommodation from "./components/Accommodation";
import Experiences from "./components/Experiences";
import Traitors from "./components/Traitors";
import Gallery from "./components/Gallery";
import Testimonials from "./components/Testimonials";
import FollowIsland from "./components/FollowIsland";
import Contact from "./components/Contact";
import PackagesPage from "./pages/PackagesPage";
import GalleryPage from "./pages/GalleryPage";
import AboutPage from "./pages/AboutPage";
import FaqsPage from "./pages/FaqsPage";
import JournalPage from "./pages/JournalPage";
import JournalPostPage from "./pages/JournalPostPage";
import AvailabilityPage from "./pages/AvailabilityPage";
import GiftVouchersPage from "./pages/GiftVouchersPage";
import PaymentResultPage from "./pages/PaymentResultPage";
import IslandWeekendPage from "./pages/IslandWeekendPage";
import TraitorsPage from "./pages/TraitorsPage";
import NotFoundPage from "./pages/NotFoundPage";

const homeSeo = {
  title: "Private Island Lodge Westport | Collanmore Island Lodge",
  description:
    "Stay on a private island in Clew Bay near Westport. Exclusive lodge sleeping 22, with private bars, beach sauna, hot tub and adventure activities.",
  path: "/",
};

// Previous WordPress website URLs → their new home on this site.
// Keeps old links, rankings and backlinks working after the switch-over.
const LEGACY_PATHS = {
  "/the-lodge": "/private-island-accommodation-westport/",
  "/the-lodge-2": "/private-island-accommodation-westport/",
  "/the-adventure": "/adventure-activities-westport/",
  "/the-entertainment": "/private-island-celebrations/",
  "/about-us": "/about-collanmore-island-lodge/",
  "/about": "/about-collanmore-island-lodge/",
  "/contact-us": "/contact/",
  "/faq": "/frequently-asked-questions/",
  "/faqs": "/frequently-asked-questions/",
  "/packages": "/private-island-packages-prices/",
  "/prices": "/private-island-packages-prices/",
  "/package-prices": "/private-island-packages-prices/",
  "/rates": "/private-island-packages-prices/",
  "/blog": "/journal/",
  "/news": "/journal/",
};

function LegacyRedirect() {
  const { pathname } = useLocation();
  let clean = pathname.toLowerCase().replace(/\/+$/, "").replace(/\.html?$/, "");
  const target = LEGACY_PATHS[clean];
  return target ? <Navigate to={target} replace /> : null;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: "80px 24px", textAlign: "center", fontFamily: "Georgia, serif" }}>
          <h1>Collanmore Island Lodge</h1>
          <p>Something went adrift. Please refresh the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function Site() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setReady(true);
      return;
    }
    const timer = setTimeout(() => setReady(true), 2100);
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <PageLayout seo={homeSeo}>
      <AnimatePresence>{!ready && <Preloader key="preloader" />}</AnimatePresence>
      <Hero ready={ready} />
      <Marquee />
      <Island />
      <TomsBar />
      <Accommodation />
      <AdventureRelax />
      <Experiences />
      <Occasions />
      <MinutesFromWestport />
      <Traitors />
      <Gallery />
      <Testimonials />
      <FollowIsland />
      <Contact />
    </PageLayout>
  );
}

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Deep link to an in-page anchor (e.g. an activity card) — scroll to it, not to top.
      const id = hash.replace("#", "");
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          if (window.__lenis) {
            try {
              window.__lenis.scrollTo(el, { offset: -100 });
            } catch {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          } else {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        }
      }, 400);
      return () => clearTimeout(timer);
    }
    if (window.__lenis) {
      try {
        window.__lenis.scrollTo(0, { immediate: true });
      } catch {
        /* lenis not ready */
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

const App = () => (
  <ErrorBoundary>
    <BrowserRouter>
      <ScrollToTop />
      <LegacyRedirect />
      <ContentProvider>
        <Routes>
          <Route path="/" element={<Site />} />
          <Route path="/private-island-accommodation-westport/" element={<LandingPage pageKey="lodge" />} />
          <Route path="/hen-parties-westport/" element={<LandingPage pageKey="hen" />} />
          <Route path="/stag-parties-westport/" element={<LandingPage pageKey="stag" />} />
          <Route path="/private-island-celebrations/" element={<LandingPage pageKey="birthdays" />} />
          <Route path="/corporate-retreats-mayo/" element={<LandingPage pageKey="corporate" />} />
          <Route path="/traitors-island-experience/" element={<TraitorsPage />} />
          <Route path="/adventure-activities-westport/" element={<LandingPage pageKey="adventure" />} />
          <Route path="/beach-sauna-westport/" element={<LandingPage pageKey="sauna" />} />
          <Route path="/private-island-weddings-mayo/" element={<LandingPage pageKey="weddings" />} />
          <Route path="/contact/" element={<ContactPage />} />
          <Route path="/private-island-packages-prices/" element={<PackagesPage />} />
          <Route path="/gallery/" element={<GalleryPage />} />
          <Route path="/about-collanmore-island-lodge/" element={<AboutPage />} />
          <Route path="/frequently-asked-questions/" element={<FaqsPage />} />
          <Route path="/journal/" element={<JournalPage />} />
          <Route path="/journal/:slug/" element={<JournalPostPage />} />
          <Route path="/availability/" element={<AvailabilityPage />} />
          <Route path="/build-your-island-weekend/" element={<IslandWeekendPage />} />
          <Route path="/gift-vouchers" element={<GiftVouchersPage />} />
          <Route path="/payment/success" element={<PaymentResultPage kind="success" />} />
          <Route path="/payment/cancel" element={<PaymentResultPage kind="cancel" />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ContentProvider>
      <ConsentBanner />
      <Toaster position="bottom-center" richColors closeButton />
    </BrowserRouter>
  </ErrorBoundary>
);

export default App;
